import type { RfqVendorInvitationRow } from '../../domain/types';

const STORAGE_PREFIX = 'rfqVendorColumnOrder:';

export function vendorQuoteOrderStorageKey(rfqId: string): string {
  return `${STORAGE_PREFIX}${rfqId}`;
}

export function vendorHasQuote(v: RfqVendorInvitationRow): boolean {
  return v.responseStatus.replace(/\s/g, '').toLowerCase() === 'quotereceived';
}

/** Default column order: quotes in the order they were received (earliest left), then vendors without a quote yet. */
export function defaultVendorColumnOrder(invited: RfqVendorInvitationRow[]): string[] {
  const withQuote = invited.filter((v) => vendorHasQuote(v));
  const without = invited.filter((v) => !vendorHasQuote(v));
  withQuote.sort((a, b) => {
    const ta = a.quoteReceivedAtUtc ? Date.parse(a.quoteReceivedAtUtc) : 0;
    const tb = b.quoteReceivedAtUtc ? Date.parse(b.quoteReceivedAtUtc) : 0;
    return ta - tb;
  });
  without.sort((a, b) => a.vendorName.localeCompare(b.vendorName, undefined, { sensitivity: 'base' }));
  return [...withQuote, ...without].map((v) => v.vendorId);
}

export function readSavedVendorOrder(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || !parsed.every((x) => typeof x === 'string')) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function mergeVendorOrder(
  saved: string[] | null,
  currentVendorIds: string[],
  defaultOrder: string[]
): string[] {
  const set = new Set(currentVendorIds);
  const fromSaved = (saved ?? []).filter((id) => set.has(id));
  const used = new Set(fromSaved);
  const rest = defaultOrder.filter((id) => set.has(id) && !used.has(id));
  return [...fromSaved, ...rest];
}

export function writeSavedVendorOrder(key: string, order: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(order));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * First vendor in procurement's column order (saved or default) that has a submitted quote.
 * Matches the leftmost ranked quoted vendor in the RFQ comparison UI.
 */
export function getFirstRankedQuotedVendorId(
  rfqId: string,
  invited: RfqVendorInvitationRow[]
): string | null {
  const storageKey = vendorQuoteOrderStorageKey(rfqId);
  const defaultOrder = defaultVendorColumnOrder(invited);
  const currentIds = invited.map((v) => v.vendorId);
  const saved = readSavedVendorOrder(storageKey);
  const orderedIds = mergeVendorOrder(saved, currentIds, defaultOrder);
  for (const id of orderedIds) {
    const v = invited.find((x) => x.vendorId === id);
    if (v && vendorHasQuote(v)) {
      return id;
    }
  }
  return null;
}
